'use client'
import { useEffect, useMemo, useState } from 'react'
import { IWorkHour } from '../../_types'
import { useTranslations } from 'next-intl'
import { useSelector } from 'react-redux'
import { IRedux } from '../../_store/types'
import dayjs from 'dayjs'
import { Box, Button, Link, Text } from '@chakra-ui/react'
import DateSelector from './DateSelector'
import TimeSelector from './TimeSelector'
import { isWorkingHour } from '../../_utils/isWorkingHour'

import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Alert } from '@/components/ui/alert'
import { formatPhoneNumber } from '../../_utils/formatPhoneNumber'

dayjs.extend(utc)
dayjs.extend(timezone)

const isDisabledDay = '2025-12-31'
const yaponamama = 'a93a4f86-b38b-404c-b1d2-7dda06aa6755'

// API week_day: 0 = Monday … 6 = Sunday (see branches Card weekdays)
const toApiWeekDay = (dayjsDay: number) => (dayjsDay === 0 ? 6 : dayjsDay - 1)

const pickEarlierTime = (a: string, b: string) => {
  const ta = dayjs(a, 'HH:mm', true)
  const tb = dayjs(b, 'HH:mm', true)
  if (!ta.isValid()) return b
  if (!tb.isValid()) return a
  return ta.isAfter(tb) ? b : a
}

export function Form({
  phone,
  scheduleData,
  interval,
  onConfirm,
}: {
  phone?: string
  interval: number
  scheduleData?: { working_hours: IWorkHour[] }
  onConfirm: (time: string) => void
}) {
  const [time, setTime] = useState<string>('')
  const [selectedDay, setSelectedDate] = useState<dayjs.Dayjs>(dayjs())

  const t = useTranslations()
  const settings = useSelector((state: IRedux) => state.settings)
  const { common, order_type, shipper } = useSelector((state: IRedux) => ({
    common: state.common,
    order_type: state.session?.order_type,
    shipper: state.session?.shipper,
  }))
  const { is_preorder, future_time } = useSelector(
    (state: IRedux) => state.checkout
  )

  const isToday = useMemo(
    () => selectedDay.isSame(dayjs(), 'day'),
    [selectedDay]
  )

  const isDelivery = order_type?.type === 'delivery'

  const deliveryMinutes = useMemo(
    () => (isDelivery ? Number(common?.branch?.delivery_time) || 0 : 0),
    [isDelivery, common?.branch?.delivery_time]
  )

  // Dostavka: faqat delivery_time; samovivoz: future_self_pickup_order_time (interval)
  const leadMinutes = useMemo(() => {
    if (isDelivery) {
      return deliveryMinutes || 60
    }
    return interval || 60
  }, [isDelivery, deliveryMinutes, interval])

  const workHours = useMemo(() => {
    const apiWeekDay = toApiWeekDay(selectedDay.day())
    return (
      scheduleData?.working_hours?.find((wh) => wh.week_day === apiWeekDay) ??
      scheduleData?.working_hours?.[apiWeekDay] ??
      null
    )
  }, [scheduleData, selectedDay])

  const schedule = useMemo(() => {
    if (workHours && !workHours.is_work) {
      return { start: '', end: '' }
    }

    let start =
      workHours?.from_time ||
      common?.branch?.work_hour_start ||
      shipper?.work_hour_start ||
      '00:00'
    let end =
      workHours?.to_time ||
      common?.branch?.work_hour_end ||
      shipper?.work_hour_end ||
      '23:59'

    if (common?.branch?.work_hour_end) {
      end = pickEarlierTime(end, common.branch.work_hour_end)
    }

    return { start, end }
  }, [workHours, common?.branch, shipper])

  const soonestMinutes = leadMinutes || 60

  const showSoonest = useMemo(() => {
    const minLead = isDelivery ? deliveryMinutes : interval
    if (!isToday || minLead <= 0) return false
    if (common?.branch?.is_work_24_7) return true
    if (!isWorkingHour(schedule.start, schedule.end)) return false

    const workStart = dayjs(schedule.start, 'HH:mm', true)
    const workEnd = dayjs(schedule.end, 'HH:mm', true)
    if (!workStart.isValid() || !workEnd.isValid()) return false
    if (workEnd.isBefore(workStart)) return true

    return !dayjs().add(soonestMinutes, 'minute').isAfter(workEnd)
  }, [isToday, isDelivery, interval, deliveryMinutes, schedule, soonestMinutes, common?.branch?.is_work_24_7])

  const canConfirm = time !== '' || showSoonest

  useEffect(() => {
    if (future_time) {
      setSelectedDate(dayjs(future_time))
      setTime(dayjs(future_time).format('YYYY-MM-DD HH:mm:ss'))
    }
  }, [is_preorder, future_time])

  const onTimeSelect = (value: string) => {
    setTime(value)
  }

  const handleDateSelect = (date: dayjs.Dayjs) => {
    setSelectedDate(date)
  }

  return (
    <Box px={4}>
      <Text color={'text'} fontSize='xl' fontWeight={700} mb={4}>
        {t('preorder_title')}
      </Text>
      <DateSelector value={selectedDay} onDateSelect={handleDateSelect} />
      {isDisabledDay === selectedDay.format('YYYY-MM-DD') &&
      settings?.data?.shipper_id === yaponamama ? (
        <NewYearError phone={phone} />
      ) : (
        <>
          <TimeSelector
            interval={interval}
            leadMinutes={leadMinutes}
            deliveryMinutes={deliveryMinutes}
            value={time}
            isToday={isToday}
            selectedDay={selectedDay}
            schedule={schedule}
            showSoonest={showSoonest}
            soonestMinutes={soonestMinutes}
            onChange={onTimeSelect}
          />
          <Box
            pb={5}
            w='full'
            left={0}
            bottom={0}
            position='sticky'
            bg={'background.card'}
          >
            <Button
              height='56px'
              width='full'
              alignSelf='end'
              bg='primary'
              color='primary.text'
              _hover={{
                bg: 'primary.hover',
              }}
              mt='8px'
              rounded={16}
              size='xl'
              disabled={!canConfirm}
              onClick={() => onConfirm(time)}
            >
              {t('save')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  )
}

function NewYearError({ phone }: { phone?: string }) {
  const t = useTranslations()
  const formattedPhoneNumber = formatPhoneNumber(phone || '')

  return (
    <Alert
      title={
        <p>
          {t('new_year_error_txt')}
          {phone && (
            <Text as='span' fontWeight={700}>
              {': '}
              <Link href={`tel:${phone}`}>{formattedPhoneNumber}</Link>
            </Text>
          )}
        </p>
      }
      mb={5}
    />
  )
}
